/**
 * ProjectSwitcher — Surface Layer (/01)
 *
 * Header control to switch the active white-label project. Reads the tenant's
 * projects (`GET /api/projects`), persists the selection in the active-project
 * store, and re-brands the surface by mapping the project's `white_label` into
 * a `TenantThemeOverride` applied through the theme provider. Defaults to the
 * first project when none is selected. Renders nothing when the tenant has no
 * projects (single-context tenants need no switcher).
 */

import { useCallback, useEffect } from 'react';
import { MenuButton, MenuItemRadioGroup } from '@carbon/react';
import { useProjects, useUserState } from '@lib/persistence/useRuntimeData';
import { useActiveProjectStore, type ActiveProject } from '@lib/persistence/useActiveProject';
import { useSurfaceTheme } from '@theme/useSurfaceTheme';
import { whiteLabelToTenantOverride } from '@theme/project-white-label';
import { useAuthStore } from '@auth/auth-store';
import type { ProjectRow } from '@contracts/runtime-endpoints.contract';

function toActive(project: ProjectRow): ActiveProject {
  return {
    id: project.id,
    slug: project.slug,
    displayLabel: project.display_label,
    ragNamespace: project.rag_namespace,
    whiteLabel: project.white_label,
  };
}

export function ProjectSwitcher() {
  const { projects } = useProjects();
  const active = useActiveProjectStore((s) => s.active);
  const setActive = useActiveProjectStore((s) => s.setActive);
  const { setTenantOverride } = useSurfaceTheme();
  const tenantId = useAuthStore((s) => s.identity?.tenantId ?? '');
  const { save: saveActiveProject } = useUserState<{ slug: string }>('active.project');

  const applyProject = useCallback(
    (project: ActiveProject | null) => {
      setActive(project);
      const override = project
        ? whiteLabelToTenantOverride(project.whiteLabel, tenantId, new Date().toISOString())
        : null;
      setTenantOverride(override);
      // Best-effort cross-device persistence (server route may be pending — fire and forget).
      void saveActiveProject({ slug: project?.slug ?? '' });
    },
    [setActive, setTenantOverride, tenantId, saveActiveProject]
  );

  // Default to the first project when none is selected.
  useEffect(() => {
    if (!active && projects.length > 0) {
      const first = projects[0];
      if (first) applyProject(toActive(first));
    }
  }, [active, projects, applyProject]);

  if (projects.length === 0) return null;

  const items = projects.map((project) => project.slug);
  const selected = active?.slug ?? items[0];

  return (
    <MenuButton kind="ghost" label={active?.displayLabel ?? 'Select project'} className="hide-at-md">
      <MenuItemRadioGroup
        label="Project"
        items={items}
        selectedItem={selected}
        onChange={(next: unknown) => {
          const match = projects.find((project) => project.slug === (next as string));
          if (match) applyProject(toActive(match));
        }}
      />
    </MenuButton>
  );
}
